import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { pipe, Array as A } from 'effect';

import Alert from '@mui/material/Alert';
import LinearProgress from '@mui/material/LinearProgress';

import Dialog from '@/components/mui/Dialog';
import CircularProgressWithBackdrop from '@/components/mui/CircularProgressWithBackdrop';
import { usePayingAccountForm } from '@/domain/paySettings/queries';
import { PayingAccountRowSchema, type PayingAccountField, type PayingAccountForm } from '@/domain/paySettings/schemas';

import PayingAccountFields from '@/pages/dokkaPay/pages/configurations/company/pages/paymentProviders/components/Providers/components/DialogPaymentProviderDetails/components/DialogAddGlobalAccount/components/PayingAccountForm';
import {
  buildDefaultValues,
  buildFormSchema,
  fromPayingAccountRow,
  toSubmitValues,
} from '@/pages/dokkaPay/pages/configurations/company/pages/paymentProviders/components/Providers/components/DialogPaymentProviderDetails/components/DialogAddGlobalAccount/components/PayingAccountForm/form';

type Props = {
  onClose: () => void;
  providerId: string;
  selectedAccount?: Record<string, unknown> | null;
  updatingAccount: boolean;
  handleCreate: (values: Record<string, string>) => void;
  handleUpdate: (values: Record<string, string> & { id: string }) => void;
};

// Own key: "Add account" (he: הוספת חשבון) must not change the shared `button.create`.
const CREATE_BUTTON = {
  id: 'configurations.company.paymentProviders.detailsModal.accountList.addModal.createBtn',
  defaultMessage: 'Add account',
};

// Frontend copy, not the description's `formTitle`: the title must match the list's "Add Payment Account" button.
const ADD_TITLE = {
  id: 'configurations.company.paymentProviders.detailsModal.accountList.addModal.title',
  defaultMessage: 'Add Payment Account',
};

const EDIT_TITLE = {
  id: 'configurations.company.paymentProviders.detailsModal.accountList.editModal.title',
  defaultMessage: 'Edit account',
};

const EDITABLE_FIELD_NAMES: ReadonlyArray<string> = ['name'];

const isEditable = (field: PayingAccountField) =>
  EDITABLE_FIELD_NAMES.includes(field.name) && !field.readOnly && !field.hidden;

type FormDialogProps = Omit<Props, 'providerId' | 'selectedAccount'> & {
  form: PayingAccountForm;
  editedAccount?: { id: string; stored: Record<string, unknown> };
};

// Mounted once the description is loaded, so useForm gets its final defaults.
const PayingAccountFormDialog: React.FC<FormDialogProps> = ({
  form: description,
  editedAccount,
  updatingAccount,
  onClose,
  handleCreate,
  handleUpdate,
}) => {
  const { formatMessage } = useIntl();
  const { fields } = description;
  const isEdit = Boolean(editedAccount);

  const defaultValues = useMemo(() => buildDefaultValues(fields, editedAccount?.stored), [fields, editedAccount]);
  // `raw`: z.object would strip hidden unvalidated fields (account_type) from the submit.
  const resolver = useMemo(
    () => zodResolver(buildFormSchema(fields, isEdit ? isEditable : undefined), undefined, { raw: true }),
    [fields, isEdit],
  );

  const form = useForm<Record<string, string>>({ resolver, mode: 'onTouched', defaultValues });

  const onSubmit = (values: Record<string, string>) =>
    editedAccount
      ? handleUpdate({ ...toSubmitValues(pipe(fields, A.filter(isEditable)), values), id: editedAccount.id })
      : handleCreate(toSubmitValues(fields, values));

  return (
    <FormProvider {...form}>
      <Dialog
        open
        maxWidth="sm"
        onClose={onClose}
        onOk={form.handleSubmit(onSubmit)}
        okBtnProps={{ disabled: !form.formState.isValid }}
        okText={formatMessage(isEdit ? { id: 'button.save', defaultMessage: 'Save' } : CREATE_BUTTON)}
        cancelText={formatMessage({ id: 'button.cancel', defaultMessage: 'Cancel' })}
        title={formatMessage(isEdit ? EDIT_TITLE : ADD_TITLE)}
      >
        <CircularProgressWithBackdrop isOpen={updatingAccount} />
        <PayingAccountFields
          fields={fields}
          isFieldDisabled={(field) => (isEdit ? !isEditable(field) : Boolean(field.readOnly))}
        />
      </Dialog>
    </FormProvider>
  );
};

const PayingAccountDialog: React.FC<Props> = (props) => {
  const { formatMessage } = useIntl();
  const { providerId, selectedAccount, onClose, ...formProps } = props;

  const editedAccount = useMemo(() => {
    const row = PayingAccountRowSchema.safeParse(selectedAccount);
    return row.success ? { id: row.data.id, stored: fromPayingAccountRow(row.data) } : undefined;
  }, [selectedAccount]);

  const { companyId = '' } = useParams();
  const formQuery = usePayingAccountForm(companyId, providerId);
  // A row that does not parse must not silently turn the edit into a create.
  // A failed background refetch keeps the cached description and the user's input.
  const isError = (formQuery.isError && !formQuery.data) || (Boolean(selectedAccount) && !editedAccount);

  if (formQuery.data && !isError) {
    return (
      <PayingAccountFormDialog {...formProps} onClose={onClose} form={formQuery.data} editedAccount={editedAccount} />
    );
  }

  const title = formatMessage(selectedAccount ? EDIT_TITLE : ADD_TITLE);

  return (
    <Dialog
      open
      maxWidth="sm"
      onClose={onClose}
      title={title}
      okText={formatMessage(selectedAccount ? { id: 'button.save', defaultMessage: 'Save' } : CREATE_BUTTON)}
      cancelText={formatMessage({ id: 'button.cancel', defaultMessage: 'Cancel' })}
      okBtnProps={{ disabled: true }}
    >
      {isError ? (
        <Alert severity="error">
          {formatMessage({
            id: 'configurations.company.paymentProviders.detailsModal.accountList.addModal.loadError',
            defaultMessage: 'Could not load the account form. Please try again later.',
          })}
        </Alert>
      ) : (
        <LinearProgress />
      )}
    </Dialog>
  );
};

export default PayingAccountDialog;
